import { environment } from '@environments/environment';
import { filterPrice, PriceOrderKey } from '@shared/services/elastic-search/mappings';
import { LogsService } from '@shared/services/logs';
import { FilterClause, PayloadModels, RangePrice, SortClause } from '../models';

/**
 * Parses a price range string (e.g., "1000-5000" or "10000-") into an Elasticsearch range object.
 * @param range - The price range string.
 *
 * @param logs - (Optional) An instance of LogsService to handle warning messages during URL processing.
 *   If provided, the service is used to log configuration issues and invalid URL errors.
 */
const parseRangePrice = (
  range: string,
  logs?: Pick<LogsService, 'warn'>
): RangePrice['range']['default_price'] | null => {
  if (!range) return null;
  const [gteStr, lteStr] = range.split('-');
  const gte = parseInt(gteStr!, 10);

  if (isNaN(gte)) {
    logs?.warn('ElasticSearchService', `Invalid 'gte' value in price range: ${gteStr}`);
    return null;
  }

  const lte = lteStr ? parseInt(lteStr, 10) : undefined;
  if (lte !== undefined && isNaN(lte)) {
    logs?.warn('ElasticSearchService', `Invalid 'lte' value in price range: ${lteStr}`);
    return null;
  }

  return lte !== undefined ? { gte, lte } : { gte };
};

/**
 * Builds the Elasticsearch query payload to fetch models based on criteria.
 * @param queryParams - The filtering and sorting parameters (PayloadModels).
 * @param logs - (Optional) An instance of LogsService to handle warning messages during URL processing.
 *   If provided, the service is used to log configuration issues and invalid URL errors.
 */
const buildPayloadModels = (langcode: string, queryParams: PayloadModels, logs?: Pick<LogsService, 'warn'>) => ({
  query: '_search',
  index: environment.integrations?.fournierWebservice?.index_models ?? '',
  body: {
    query: {
      bool: {
        filter: [
          {
            term: {
              search_api_language: langcode,
            },
          },
          {
            term: {
              search_api_datasource: 'entity:node',
            },
          },
          ...buildModelFilters(queryParams, logs),
        ],
      },
    },
    sort: buildModelSort(queryParams),
    from: queryParams.itemFrom,
    size: queryParams.itemsToFetch,
    _source: [
      'nid',
      'nom_modele',
      'default_price_with_currency',
      'default_ecopart_with_currency',
      'field_visuels_principaux',
      'implantation_principale',
      'short_ref',
      'url',
    ],
  },
});

/**
 * Constructs the filter clauses for the model query based on input parameters.
 * @param params - The model query parameters (PayloadModels).
 * @param logs - (Optional) An instance of LogsService to handle warning messages during URL processing.
 *   If provided, the service is used to log configuration issues and invalid URL errors.
 */
const buildModelFilters = (params: PayloadModels, logs?: Pick<LogsService, 'warn'>): FilterClause[] => {
  const filters: FilterClause[] = [];

  // Price Range Filter
  if (params.rangePrice) {
    const priceRange = parseRangePrice(params.rangePrice, logs);
    if (priceRange) {
      filters.push({ range: { default_price: priceRange } });
    }
  }

  // Layout Filter
  if (params.layout) {
    filters.push({ term: { implantation_principale: params.layout } });
  }

  // Front Filter (Nested Query)
  if (params.front) {
    filters.push({
      nested: {
        path: 'field_visuels_principaux',
        query: { term: { 'field_visuels_principaux.filters.name.keyword': params.front } },
      },
    });
  }

  // Space Filter
  if (params.space) {
    filters.push({ term: { espace: params.space } });
  }

  return filters;
};

/**
 * Constructs the sort clauses for the model query based on input parameters.
 * @param params - The model query parameters (PayloadModels).
 */
const buildModelSort = (params: PayloadModels): SortClause[] => {
  const sortClauses: SortClause[] = [];

  // Standard Sort (e.g., by price)
  if (params.sort) {
    const order = filterPrice(params.sort as PriceOrderKey); // Translates string like 'price_ascending' to 'asc' | 'desc'
    if (order) {
      sortClauses.push({ default_price: { order } });
    }
  }

  // Custom Brand Selection Sort (by weight)
  if (params.enableCustomBrandSelection) {
    sortClauses.push({ field_weight: { order: 'asc' } });
  }

  return sortClauses;
};

export const GET_KITCHENS_QUERY = (langcode: string, query: PayloadModels, logs?: Pick<LogsService, 'warn'>) =>
  buildPayloadModels(langcode, query, logs);
